import React from "react";
import { useTranslation } from "react-i18next";
import { SFStack } from "../../grid";
import SFGenericSearch from "../../typeahead/genericSearch";
import getStyled from "../../../hooks/getStyled";
import PropTypes from "prop-types";
import SFButton from "../../button";
import CheckboxFilter, { CheckboxFilterPropTypes } from "./checkboxFilter";

const { styled } = getStyled();

const StyledSFGenericSearch = styled(SFGenericSearch)(({ theme }) => ({
  "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
    margin: 0,
    padding: 0,
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.grey50.main,
    "& fieldset": {
      borderColor: theme.palette.grey250.main,
      borderRadius: "4px",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.grey250.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.grey250.main,
    },
  },
  "& .MuiInputAdornment-root": {
    top: "auto !important",
    left: "10px !important",
  },
}));

const StyledStack = styled(SFStack)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey100.main,
}));

const FilterPanel = ({ dropdownFilter, checkboxFilter, onChange }) => {
  const { t } = useTranslation();

  // Function to calculate total number of selected filters (dropdown selections + checked checkboxes)
  const calculateTotalSelectedFilters = (dropdownFilter, checkboxFilter) => {
    // Count the selected values from dropdown/generic search
    const selectedValuesCount = dropdownFilter?.selectedValues?.length || 0;

    // Count the checked checkboxes
    let checkedCheckboxesCount = 0;
    if (
      checkboxFilter?.checkboxes &&
      Array.isArray(checkboxFilter.checkboxes)
    ) {
      checkboxFilter.checkboxes.forEach((checkbox) => {
        if (checkbox.checked === true) {
          checkedCheckboxesCount++;
        }
      });
    }

    // Return the total count
    return selectedValuesCount + checkedCheckboxesCount;
  };
  // Convert the values to an array of objects with a value key (empty array if no values are present)
  // to match the expected format of the SFGenericSearch component
  // dropdownFilter values are the possible options
  const getOptions = (dropdownFilter) => {
    return dropdownFilter?.values?.map((option) => ({ value: option })) || [];
  };
  // getOptions function only needed for SFGenericSearch

  const hasDropdownFilter =
    dropdownFilter && Object.keys(dropdownFilter).length > 0;
  const hasCheckboxFilter =
    checkboxFilter && Object.keys(checkboxFilter).length > 0; // Handle checkbox onChange - now we have access to all updated checkboxes
  const handleCheckboxChange = (checkboxValue, checked, updatedCheckboxes) => {
    if (onChange) {
      onChange({
        type: "checkbox",
        checkboxValue, // Preserve the original checkbox value
        checked,
        isBulkOperation: false, // This is a single checkbox change
        updatedCheckboxes,
        dropdownValues: dropdownFilter?.selectedValues || [],
      });
    }
  };

  return (
    <StyledStack spacing={3} direction="column" alignItems="center">
      {hasDropdownFilter && (
        <StyledSFGenericSearch
          fullWidth
          onClose={(selectedValues) => {
            if (onChange) {
              onChange({
                type: "dropdown",
                dropdownValues: selectedValues,
                updatedCheckboxes: checkboxFilter?.checkboxes || [],
              });
            }
          }}
          selectedValues={dropdownFilter?.selectedValues || []}
          source={getOptions(dropdownFilter)}
          placeholder={dropdownFilter?.placeholder}
          hasSearchIcon
          multiple
          size="small"
          variant="outlined"
        />
      )}
      {/* Render checkbox filter */}
      {hasCheckboxFilter && (
        <CheckboxFilter
          label={checkboxFilter?.label}
          checkboxes={checkboxFilter?.checkboxes}
          onChange={handleCheckboxChange}
        />
      )}
      <SFStack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
        <SFButton
          variant="text-link"
          disabled={
            calculateTotalSelectedFilters(dropdownFilter, checkboxFilter) <= 0
          }
          onClick={() => {
            // Create array of all checkboxes with checked=false
            let uncheckedCheckboxes = [];
            if (
              hasCheckboxFilter &&
              checkboxFilter?.checkboxes &&
              Array.isArray(checkboxFilter.checkboxes)
            ) {
              uncheckedCheckboxes = checkboxFilter.checkboxes.map(
                (checkbox) => ({
                  ...checkbox,
                  checked: false,
                })
              );
            }

            if (onChange) {
              onChange({
                type: "clear",
                isBulkOperation: true,
                dropdownValues: [],
                updatedCheckboxes: uncheckedCheckboxes,
              });
            }
          }}
        >
          {t("components.datagrid.panelSearch.clearFilters")}
        </SFButton>
      </SFStack>
    </StyledStack>
  );
};

export default FilterPanel;

/**
 * PropTypes definition for the FilterPanel component.
 * This component displays both dropdown and checkbox filters in a panel.
 */
FilterPanel.propTypes = {
  /**
   * Configuration for the dropdown filter.
   * Optional - if not provided, the dropdown filter will not be displayed.
   */
  dropdownFilter: PropTypes.shape({
    /**
     * Placeholder text displayed in the dropdown when no items are selected.
     */
    placeholder: PropTypes.string,

    /**
     * Array of currently selected values in the dropdown.
     */
    selectedValues: PropTypes.arrayOf(PropTypes.string),

    /**
     * Array of all available options for the dropdown.
     */
    values: PropTypes.arrayOf(PropTypes.string),
  }),

  /**
   * Configuration for the checkbox filters.
   * Optional - if not provided, the checkbox filters will not be displayed.
   * @see CheckboxFilterPropTypes for detailed documentation.
   */
  checkboxFilter: CheckboxFilterPropTypes,
  /**
   * Unified change handler for both dropdown and checkbox filters.
   * See SFPanelSearch component for detailed documentation of the handler structure.
   *
   * This handler receives different payloads based on the type of change:
   * - checkbox: for individual or bulk checkbox changes
   * - dropdown: for dropdown selection changes
   * - clear: when clearing all filters
   */
  onChange: PropTypes.func.isRequired,
};
